/**
 * Lead attribution fields shared by the contact form, the server and tests.
 *
 * Two sets are kept per visitor:
 * - "latest": the most recent visit that carried a utm parameter or click id.
 *   A visit without tracking never touches it. Sent empty when older than 90 days.
 * - "first": the very first visit. Written once, never overwritten.
 *
 * Field names are the exact lowercase names agreed with the CRM owners and are
 * used both as hidden form fields and as keys in the JSON submission.
 */

export const TRACKING_FIELD_LIMITS = {
  utm_source: 100,
  utm_medium: 100,
  utm_campaign: 100,
  utm_content: 100,
  utm_term: 100,
  gclid: 255,
  gbraid: 255,
  wbraid: 255,
  fbclid: 255,
  landing_url: 500,
  first_utm_source: 100,
  first_utm_medium: 100,
  first_utm_campaign: 100,
  first_landing_url: 500,
  first_seen_at: 40,
} as const;

export type TrackingField = keyof typeof TRACKING_FIELD_LIMITS;
export type TrackingPayload = Record<TrackingField, string>;

export const TRACKING_FIELDS = Object.keys(TRACKING_FIELD_LIMITS) as TrackingField[];

const LATEST_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
] as const;

type LatestParam = (typeof LATEST_PARAMS)[number];

export const LATEST_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

export interface StoredAttribution {
  latest?: Partial<Record<LatestParam | "landing_url", string>>;
  /** Epoch milliseconds of the latest tracked visit */
  latestSeenAt?: number;
  first?: {
    first_utm_source?: string;
    first_utm_medium?: string;
    first_utm_campaign?: string;
    first_landing_url?: string;
    first_seen_at?: string;
  };
}

/** Empty when missing or longer than the field limit. Values are never cut. */
export function fitTrackingValue(field: TrackingField, value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") return "";
  return trimmed.length > TRACKING_FIELD_LIMITS[field] ? "" : trimmed;
}

/** Origin and path only — never query string, hash or form data. */
export function toLandingUrl(url: URL): string {
  return `${url.origin}${url.pathname}`;
}

/** ISO 8601 with the visitor's local offset, e.g. 2026-09-28T14:05:00+02:00 */
export function toIsoWithOffset(date: Date): string {
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, "0");
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `${sign}${pad(offsetMinutes / 60)}:${pad(offsetMinutes % 60)}`
  );
}

/** Applies one page view to the stored attribution and returns the new state. */
export function captureVisit(
  stored: StoredAttribution,
  url: URL,
  now: Date,
): StoredAttribution {
  const next: StoredAttribution = { ...stored };
  const landingUrl = toLandingUrl(url);

  const latest: NonNullable<StoredAttribution["latest"]> = {};
  for (const param of LATEST_PARAMS) {
    const value = fitTrackingValue(param, url.searchParams.get(param));
    if (value) latest[param] = value;
  }

  if (Object.keys(latest).length > 0) {
    latest.landing_url = fitTrackingValue("landing_url", landingUrl);
    // Replace the whole set so ids from an older campaign never linger.
    next.latest = latest;
    next.latestSeenAt = now.getTime();
  }

  if (!stored.first?.first_seen_at) {
    next.first = {
      first_utm_source: fitTrackingValue("first_utm_source", url.searchParams.get("utm_source")),
      first_utm_medium: fitTrackingValue("first_utm_medium", url.searchParams.get("utm_medium")),
      first_utm_campaign: fitTrackingValue("first_utm_campaign", url.searchParams.get("utm_campaign")),
      first_landing_url: fitTrackingValue("first_landing_url", landingUrl),
      first_seen_at: toIsoWithOffset(now),
    };
  }

  return next;
}

/** Builds all 15 fields for a submission. Missing values are empty strings. */
export function buildTrackingPayload(
  stored: StoredAttribution,
  now: Date,
): TrackingPayload {
  const latestIsFresh =
    typeof stored.latestSeenAt === "number" &&
    now.getTime() - stored.latestSeenAt <= LATEST_MAX_AGE_MS;
  const latest = latestIsFresh ? stored.latest ?? {} : {};
  const first = stored.first ?? {};
  const source: Record<string, unknown> = { ...latest, ...first };

  const payload = {} as TrackingPayload;
  for (const field of TRACKING_FIELDS) {
    payload[field] = fitTrackingValue(field, source[field]);
  }
  return payload;
}

export function emptyTrackingPayload(): TrackingPayload {
  return buildTrackingPayload({}, new Date(0));
}

/**
 * Folds attribution held in memory (before cookie consent) into stored
 * attribution once consent is given, following the same rules as captureVisit.
 */
export function mergeAttribution(
  stored: StoredAttribution,
  pending: StoredAttribution,
): StoredAttribution {
  const next: StoredAttribution = { ...stored };
  if (
    pending.latest &&
    typeof pending.latestSeenAt === "number" &&
    (typeof stored.latestSeenAt !== "number" || pending.latestSeenAt >= stored.latestSeenAt)
  ) {
    next.latest = pending.latest;
    next.latestSeenAt = pending.latestSeenAt;
  }
  if (!stored.first?.first_seen_at && pending.first?.first_seen_at) {
    next.first = pending.first;
  }
  return next;
}
